import { useEffect } from 'react';

import {
  CROSSHAIR_CURSOR_OPTIONS,
  DEFAULT_CURSOR_OPTIONS,
  GRAB_CURSOR_OPTIONS,
  MARKERS_INERT_CLASS,
} from '../drawingConstants';
import { ensureMarkersInertStyle } from '../drawingUtilities';
import { DrawingMode, Interaction } from '../geometry';

type UseDrawingCursorArgs = {
  map: google.maps.Map | null;
  mode: Interaction;
  // Non-null only while armed to draw (no shape yet); drives the crosshair.
  drawKind: DrawingMode | null;
};

// Drives the map cursor for the current interaction and makes data markers
// inert while grabbing so the grab click falls through to the map.
const useDrawingCursor = ({ map, mode, drawKind }: UseDrawingCursorArgs) => {
  useEffect(() => {
    if (!map) {
      return undefined;
    }

    switch (mode) {
      case 'grab':
        map.setOptions(GRAB_CURSOR_OPTIONS);
        break;
      case 'point':
        map.setOptions(CROSSHAIR_CURSOR_OPTIONS);
        break;
      case 'circle':
      case 'polygon':
      case 'linestring':
        // Crosshair only while armed to draw; once a shape exists drawKind is
        // null and the default cursor lets the user edit/interact normally.
        // Double-click zoom stays off for the whole time a draw tool is
        // selected: a double-click finishes a polygon/line, and a quick
        // two-click circle would otherwise also zoom the map.
        map.setOptions({
          ...(drawKind ? CROSSHAIR_CURSOR_OPTIONS : DEFAULT_CURSOR_OPTIONS),
          disableDoubleClickZoom: true,
        });
        break;
      default:
        map.setOptions(DEFAULT_CURSOR_OPTIONS);
    }
    // Restore the default cursor and double-click zoom on unmount — the <Map>
    // instance is reused (reuseMaps), so a stale crosshair/grab cursor or
    // disabled double-click zoom would otherwise persist after drawing is
    // toggled off.
    return () => map.setOptions(DEFAULT_CURSOR_OPTIONS);
  }, [map, mode, drawKind]);

  useEffect(() => {
    if (!map || mode !== 'grab') {
      return undefined;
    }

    ensureMarkersInertStyle();
    const div = map.getDiv();
    div.classList.add(MARKERS_INERT_CLASS);
    return () => div.classList.remove(MARKERS_INERT_CLASS);
  }, [map, mode]);
};

export default useDrawingCursor;
